/**
 * Pure helpers for NumberRulesInput: labels, validation, summary.
 * Keeps the main component focused on rendering and wiring.
 */

import type { TFunction } from "i18next";
import type { NumberRule } from "../forms/number-rules-input";

/** Stock / movements APIs use PascalCase keys (NetWeight, GrossWeight, NettWeight). */
function isNetWeightColumnKey(columnKey: string | undefined): boolean {
  if (!columnKey) return false;
  const k = columnKey.toLowerCase();
  return k === "netweight" || k === "nettweight";
}

function isGrossWeightColumnKey(columnKey: string | undefined): boolean {
  if (!columnKey) return false;
  return columnKey.toLowerCase() === "grossweight";
}

/** Resolve the filter label key and then the translated label for the trigger. */
export function getFilterLabel(
  columnKey: string | undefined,
  columnLabel: string,
  t: TFunction
): string {
  const key = isNetWeightColumnKey(columnKey)
    ? "filterLabelNetWeight"
    : isGrossWeightColumnKey(columnKey)
      ? "filterLabelGrossWeight"
      : "filterLabel";
  return key === "filterLabel" ? t("filterLabel", { columnLabel }) : t(key);
}

/** Placeholder for numeric rule inputs in the popover (net vs gross vs generic). */
export function getNumberRuleValuePlaceholder(
  columnKey: string | undefined,
  t: TFunction
): string {
  if (isNetWeightColumnKey(columnKey)) return t("valuePlaceholderNetWeight");
  if (isGrossWeightColumnKey(columnKey)) return t("valuePlaceholderGrossWeight");
  return t("valuePlaceholder");
}

/** Result of validating rules before apply. */
export type ValidationResult =
  | { valid: true }
  | { valid: false; errorKey: "errorBothLessThan" | "errorBothGreaterThan" | "errorInvalidRange" };

/**
 * Validates rules: same-operator check and range validity (gte value <= lte value).
 * Call before committing on Apply.
 */
export function validateRules(rules: NumberRule[]): ValidationResult {
  if (rules.length === 2 && rules[0].operator === rules[1].operator) {
    return {
      valid: false,
      errorKey:
        rules[0].operator === "lte" ? "errorBothLessThan" : "errorBothGreaterThan",
    };
  }
  if (rules.length === 2) {
    const gteRule = rules.find((r) => r.operator === "gte");
    const lteRule = rules.find((r) => r.operator === "lte");
    const gteVal = gteRule?.value.trim();
    const lteVal = lteRule?.value.trim();
    if (gteVal && lteVal) {
      const min = Number(gteVal);
      const max = Number(lteVal);
      if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
        return { valid: false, errorKey: "errorInvalidRange" };
      }
    }
  }
  return { valid: true };
}

/** Whether there is at least one rule with a non-empty value. */
export function hasValidRule(rules: NumberRule[]): boolean {
  return rules.some((r) => r.value.trim() !== "");
}

/** Format rules for the trigger summary (e.g. "> 10, < 20"). */
export function formatSummary(
  rules: NumberRule[],
  filterLabel: string
): string {
  if (rules.length === 0) return filterLabel;
  const parts = rules
    .filter((r) => r.value.trim() !== "")
    .map((r) => (r.operator === "gte" ? `> ${r.value}` : `< ${r.value}`));
  return parts.length > 0 ? parts.join(", ") : filterLabel;
}

/** Rules to display in the popover (always at least one row). */
export function getRulesToShow(rules: NumberRule[]): NumberRule[] {
  return rules.length === 0
    ? [{ operator: "gte", value: "" }]
    : rules;
}
